import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { VideoCard } from "@/components/VideoCard";
import { DiscoverButton } from "@/components/DiscoverButton";
import { Search, SlidersHorizontal } from "lucide-react";

const PAGE = 24;

export default function Library() {
  const [params, setParams] = useSearchParams();
  const [videos, setVideos] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [facets, setFacets] = useState({ subjects: [], years: [], locations: [], instructors: [], categories: [], category_counts: {}, content_types: [], areas: [], collection_groups: [] });
  const [counts, setCounts] = useState({ catalogued: 0, viewable: 0 });
  const [q, setQ] = useState(params.get("q") || "");
  const [category, setCategory] = useState(params.get("category") || "all");
  const [subject, setSubject] = useState("all");
  const [year, setYear] = useState("all");
  const [location, setLocation] = useState("all");
  const [instructor, setInstructor] = useState("all");
  const [contentType, setContentType] = useState("all");
  const [area, setArea] = useState("all");
  const [collectionGroup, setCollectionGroup] = useState("all");
  const [sort, setSort] = useState("featured");
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const offsetRef = useRef(0);

  useEffect(() => { api.get("/videos/facets").then(({ data }) => setFacets(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/stats/public").then(({ data }) => setCounts(data)).catch(() => {}); }, []);

  const buildParams = useCallback((offset) => {
    const p = new URLSearchParams({ sort, limit: PAGE, offset });
    if (q) p.set("q", q);
    if (category !== "all") p.set("category", category);
    if (subject !== "all") p.set("subject", subject);
    if (year !== "all") p.set("year", year);
    if (location !== "all") p.set("location", location);
    if (instructor !== "all") p.set("instructor", instructor);
    if (contentType !== "all") p.set("content_type", contentType);
    if (area !== "all") p.set("area", area);
    if (collectionGroup !== "all") p.set("collection_group", collectionGroup);
    return p;
  }, [q, category, subject, year, location, instructor, contentType, area, collectionGroup, sort]);

  const load = useCallback(async () => {
    setLoading(true);
    offsetRef.current = 0;
    const { data } = await api.get(`/videos?${buildParams(0).toString()}`);
    setVideos(data.videos); setTotal(data.count); setHasMore(data.has_more); setLoading(false);
  }, [buildParams]);

  const loadMore = async () => {
    const next = offsetRef.current + PAGE;
    offsetRef.current = next;
    const { data } = await api.get(`/videos?${buildParams(next).toString()}`);
    setVideos((prev) => [...prev, ...data.videos]); setHasMore(data.has_more);
  };

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const pickCategory = (c) => {
    setCategory(c);
    if (c === "all") setParams({}); else setParams({ category: c });
  };

  const tab = (val, label) => (
    <button key={val} data-testid={`cat-tab-${val}`} onClick={() => pickCategory(val)}
      className={`px-5 py-2 text-sm whitespace-nowrap transition-colors border ${
        category === val ? "bg-[#C5A059] text-[#0A0B0E] border-[#C5A059]" : "text-[#8A8F98] border-white/10 hover:border-[#C5A059]/40"
      }`}>
      {label}
    </button>
  );

  const sel = (tid, val, setVal, allLabel, opts, fmt) => (
    <select data-testid={tid} value={val} onChange={(e) => setVal(e.target.value)}
      className="bg-transparent text-[#EBE9E1] outline-none text-sm border border-white/15 px-3 py-2.5 rounded-sm">
      <option value="all" className="bg-[#121418]">{allLabel}</option>
      {opts.map((o) => <option key={o} value={o} className="bg-[#121418]">{fmt ? fmt(o) : o}</option>)}
    </select>
  );

  return (
    <div className="min-h-screen grain">
      <Navbar />
      <div className="px-6 md:px-12 lg:px-24 mx-auto max-w-7xl py-14">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 fade-up">
          <div>
            <p className="text-sm tracking-[0.3em] uppercase text-[#C5A059] mb-3">The Collection</p>
            <h1 className="font-serif text-5xl md:text-6xl text-[#EBE9E1]">Video Library</h1>
            <p className="text-[#8A8F98] mt-3 text-lg" data-testid="archive-counts">{counts.viewable || total} recordings available to view · {counts.catalogued} catalogued in the archive</p>
          </div>
          <DiscoverButton variant="ghost" />
        </div>

        <div className="flex gap-3 overflow-x-auto pb-3 mb-6">
          {tab("all", "All")}
          {facets.categories.map((c) => tab(c, `${c} (${facets.category_counts[c] || 0})`))}
        </div>

        <div className="border border-white/10 bg-[#121418] p-4 mb-10">
          <div className="flex flex-col lg:flex-row gap-4 items-stretch lg:items-center">
            <div className="flex items-center gap-3 flex-1 border border-white/10 lg:border-0 lg:border-r px-3 lg:pr-4 py-2">
              <Search size={18} className="text-[#8A8F98]" />
              <input data-testid="library-search" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search by title, technique, seminar, dojo…"
                className="bg-transparent outline-none text-[#EBE9E1] placeholder:text-[#555] w-full" />
            </div>
            <button data-testid="toggle-filters" onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 text-sm text-[#8A8F98] hover:text-[#C5A059] transition-colors px-3 py-2 border border-white/10">
              <SlidersHorizontal size={16} /> Filters
            </button>
            <select data-testid="filter-sort" value={sort} onChange={(e) => setSort(e.target.value)}
              className="bg-transparent text-[#EBE9E1] outline-none text-sm border border-white/15 px-3 py-2.5 rounded-sm">
              <option value="featured" className="bg-[#121418]">Featured</option>
              <option value="newest" className="bg-[#121418]">Newest first</option>
              <option value="oldest" className="bg-[#121418]">Oldest first</option>
              <option value="az" className="bg-[#121418]">A–Z</option>
            </select>
          </div>
          {showFilters && (
            <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t border-white/10">
              {facets.collection_groups && facets.collection_groups.length > 1 &&
                sel("filter-collection-group", collectionGroup, setCollectionGroup, "All collections", facets.collection_groups)}
              {sel("filter-content-type", contentType, setContentType, "All content types", facets.content_types)}
              {sel("filter-area", area, setArea, "All areas of study", facets.areas)}
              {sel("filter-subject", subject, setSubject, "All techniques / subjects", facets.subjects)}
              {sel("filter-year", year, setYear, "Any year", facets.years)}
              {sel("filter-location", location, setLocation, "Any location / dojo", facets.locations)}
              {sel("filter-instructor", instructor, setInstructor, "Any source / instructor", facets.instructors)}
            </div>
          )}
        </div>

        {loading ? (
          <p className="text-[#8A8F98] py-20 text-center">Loading the archive…</p>
        ) : videos.length === 0 ? (
          <p data-testid="library-empty" className="text-[#8A8F98] py-20 text-center">No videos match your search.</p>
        ) : (
          <>
            <div data-testid="library-grid" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
              {videos.map((v, i) => <VideoCard key={v.id} video={v} index={i} />)}
            </div>
            {hasMore && (
              <div className="text-center mt-16">
                <button data-testid="load-more" onClick={loadMore} className="btn-ghost px-10 py-4">
                  Load more recordings
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
